import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ItemType } from "@jasper/client-models";
import type { ToothFormat } from "@jasper/forms";
import {
  itemPreferencesToInput,
  type UpdateProductArgs,
} from "@jasper/use-cases";

import {
  getReassignments,
  planTeethSelection,
  resolveBulkGroup,
  type BulkGroup,
} from "../../bulkTeethPlan";
import { BULK_PRODUCTS_QUERY_PARAM, buildProductPath } from "../../paths";
import type {
  ConfigurableProduct,
  OrderConfiguration,
  TeethMutationStrategy,
} from "../types";

export type FormatTeethSelection<T extends ToothFormat> = (args: {
  teeth: T[];
  product: ConfigurableProduct;
  itemType: ItemType;
}) => Promise<UpdateProductArgs> | UpdateProductArgs;

type UseTeethSelectionSubmissionArgs<T extends ToothFormat> = {
  products: ConfigurableProduct[];
  itemType: ItemType;
  orderConfiguration: OrderConfiguration;
  formatTeethSelection: FormatTeethSelection<T>;
  mutationStrategy?: TeethMutationStrategy;
};

type NormalizedAssignment = {
  teeth: number[];
  product: ConfigurableProduct | null;
  targetProductType: ConfigurableProduct["type"];
};

const toToothNumber = (tooth: ToothFormat) =>
  typeof tooth === "number" ? tooth : tooth.tooth;

/**
 * Saves a teeth selection made over the products a step configures, one product
 * per selected teeth group.
 *
 * A single group selected on a single product updates it in place. Otherwise the
 * selection is planned against the products: kept products are moved onto the
 * groups left uncovered, missing ones are created from the first product, and the
 * surplus is deleted. The URL then follows the products that survived, the first
 * product leading them if it is still there.
 */
export const useTeethSelectionSubmission = <T extends ToothFormat>({
  products,
  itemType,
  orderConfiguration,
  formatTeethSelection,
  mutationStrategy,
}: UseTeethSelectionSubmissionArgs<T>) => {
  const { t } = useTranslation("product-workflows");
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { addOrderProducts, updateProducts, removeOrderProducts, order } =
    orderConfiguration;

  const [leaderProduct] = products;

  const followSurvivingGroup = (survivingGroup: BulkGroup | null) => {
    if (!survivingGroup) {
      return;
    }

    if (survivingGroup.leaderProductId !== leaderProduct.id) {
      navigate(
        buildProductPath(
          order!.id,
          survivingGroup.leaderProductId,
          survivingGroup.bulkProductFollowerIds
        )
      );
      return;
    }

    setSearchParams(prev => ({
      ...prev,
      [BULK_PRODUCTS_QUERY_PARAM]:
        survivingGroup.bulkProductFollowerIds.join(","),
    }));
  };

  const updateInPlace = async (teeth: T[]) => {
    try {
      await updateProducts([
        await formatTeethSelection({ teeth, product: leaderProduct, itemType }),
      ]);
    } catch {
      notifications.show({
        title: t("common.errorNotificationTitle"),
        message: t("common.errorNotificationMessage"),
        color: "red",
      });
    }
  };

  const replanProducts = async (selectedTeethGroups: T[][]) => {
    const initialTeeth = selectedTeethGroups.flat();
    const findInitialTooth = (tooth: number) =>
      initialTeeth.find(initialTooth => toToothNumber(initialTooth) === tooth)!;

    const initialPlan = planTeethSelection({
      selectedTeethGroups: selectedTeethGroups.map(group =>
        group.map(toToothNumber)
      ),
      products,
    });

    const forcedProductsToDeleteById = new Map<string, ConfigurableProduct>();

    const normalizedAssignments: NormalizedAssignment[] =
      initialPlan.assignments.map(assignment => {
        const targetProductType = mutationStrategy
          ? mutationStrategy.getTargetProductType(assignment.teeth)
          : leaderProduct.type;

        if (!assignment.product) {
          return { teeth: assignment.teeth, product: null, targetProductType };
        }

        const canReuseProduct = mutationStrategy?.canReuseProduct
          ? mutationStrategy.canReuseProduct({
              currentProductType: assignment.product.type,
              targetProductType,
            })
          : true;

        if (!canReuseProduct) {
          forcedProductsToDeleteById.set(
            assignment.product.id,
            assignment.product
          );
          return { teeth: assignment.teeth, product: null, targetProductType };
        }

        return {
          teeth: assignment.teeth,
          product: assignment.product,
          targetProductType,
        };
      });

    const plan = {
      assignments: normalizedAssignments.map(({ teeth, product }) => ({
        teeth,
        product,
      })),
      productsToDelete: [
        ...initialPlan.productsToDelete,
        ...forcedProductsToDeleteById.values(),
      ],
    };

    const assignmentsToCreate = normalizedAssignments.filter(
      assignment => !assignment.product
    );
    const { productsToDelete } = plan;

    const inheritedItemPreferences = itemPreferencesToInput(
      leaderProduct.itemPreferences
    );
    const knownProductIds = new Set(
      (order?.products ?? []).map(product => product.id)
    );

    let createdProducts: { id: string; teeth: ReadonlyArray<number> }[] = [];

    try {
      if (assignmentsToCreate.length) {
        const productsToCreate = await Promise.all(
          assignmentsToCreate.map(async assignment => {
            const update = await formatTeethSelection({
              teeth: assignment.teeth.map(findInitialTooth),
              product: leaderProduct,
              itemType,
            });

            return {
              type: assignment.targetProductType,
              teeth: assignment.teeth,
              items: update.items ?? [],
            };
          })
        );

        const result = await addOrderProducts(productsToCreate);

        createdProducts = (result.data?.addOrderProducts.products ?? []).filter(
          product => !knownProductIds.has(product.id)
        );
      }

      // Teeth carrying a value (e.g. implanted / pontic) can change without moving,
      // so every kept product must be saved again, not only the moved ones.
      const hasTeethValues = initialTeeth.some(
        tooth => typeof tooth !== "number"
      );
      const productsToUpdate = hasTeethValues
        ? plan.assignments.filter(
            (
              assignment
            ): assignment is {
              teeth: number[];
              product: ConfigurableProduct;
            } => !!assignment.product
          )
        : getReassignments(plan);

      const updates = await Promise.all([
        ...productsToUpdate.map(({ product, teeth }) =>
          formatTeethSelection({
            teeth: teeth.map(findInitialTooth),
            product,
            itemType,
          })
        ),
        ...(inheritedItemPreferences
          ? createdProducts.map(({ id }) => ({
              productId: id,
              itemPreferences: inheritedItemPreferences,
            }))
          : []),
      ]);

      if (updates.length) {
        await updateProducts(updates);
      }

      if (productsToDelete.length) {
        await removeOrderProducts(productsToDelete.map(({ id }) => id));
      }
    } catch {
      notifications.show({
        title: t("bulk.errorNotificationTitle"),
        message: t("bulk.errorNotificationMessage"),
        color: "red",
      });
      return;
    }

    followSurvivingGroup(
      resolveBulkGroup({
        plan,
        leaderProductId: leaderProduct.id,
        createdProducts,
      })
    );
  };

  return async (selectedTeethGroups: T[][]) => {
    if (products.length === 1 && selectedTeethGroups.length === 1) {
      await updateInPlace(selectedTeethGroups[0]);
      return;
    }

    await replanProducts(selectedTeethGroups);
  };
};
